// AutoGraphs.tsx
/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useEffect, useMemo, useState, useContext } from 'react';
import { Checkbox } from 'primereact/checkbox';
import GraphStepsDistribution from '../../container-for-graphics/components/graph-steps-distribution/graph-steps-distribution';
import { getFlexosTimeGraph } from '@marsinet/commons';
import BrushGraphs from '../../../../../../../../app/components/brush-graphs/brush-graphs';
import { TFunction } from 'i18next';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import RoleBasedMultiLineChart from 'apps/marsinet/src/app/components/role-based-multi-line-chart/RoleBasedMultiLineChart';
import EmptyState from '../graphs/empty-grphs';
import { ROLES } from '@marsinet/commons';
import { DeviceModelType } from 'apps/marsinet/src/app/models/devicesModels';

//Función falsa sin datos.
const noop = (_: boolean) => {
  /*intentionally empty*/
};

type GraphType = 'steps' | 'time';

type AutoGraphsProps = {
  t: TFunction<'translation'>;
  raw: any; // payload AUTO (graphData.auto.raw)
  motorsAng: any[] | null; // graphData.auto.motorsAng
  deviceModel: DeviceModelType | null;
  timeWalking: number;
  stepsTotal: number;

  //brush
  startIndex: number;
  endIndex: number;
  onBrushChange: (r: { startIndex: number; endIndex: number }) => void;
  onBaseLenChange: (len: number) => void;
};

function hasValidData(data: any) {
  if (!data) return false;
  if (Array.isArray(data)) return data.length > 0;
  if (typeof data === 'object') return Object.keys(data).length > 0;
  return false;
}

function AutoGraphsImpl(props: Readonly<AutoGraphsProps>) {
  const { t, raw, motorsAng, onBaseLenChange } = props;
  const { role } = useContext(MarsinetContext);

  useEffect(() => {
    onBaseLenChange(motorsAng?.length ?? 0);
  }, [motorsAng?.length, onBaseLenChange]);

  // Gráficos de distribución (flexos)
  const [selectedHipDistributionGraph, setSelectedHipDistributionGraph] =
    useState<GraphType>('steps');
  const [selectedKneeDistributionGraph, setSelectedKneeDistributionGraph] =
    useState<GraphType>('steps');

  const filteredMotorsAng = useMemo(() => {
    const src = motorsAng ?? [];
    return src.slice(props.startIndex, props.endIndex + 1);
  }, [motorsAng, props.startIndex, props.endIndex]);

  const keyMapUser = useMemo(() => {
    if (!filteredMotorsAng || filteredMotorsAng.length === 0) return {};

    const sample = filteredMotorsAng[0];

    return Object.keys(sample)
      .filter((key) => key.startsWith('ci_')) // 🔥 SOLO CI
      .reduce(
        (acc, key) => {
          acc[key] = key; // mapping 1:1
          return acc;
        },
        {} as Record<string, string>
      );
  }, [filteredMotorsAng]);

  const keyMapAdmin = useMemo(() => {
    if (!filteredMotorsAng || filteredMotorsAng.length === 0) return {};

    const sample = filteredMotorsAng[0];

    return Object.keys(sample)
      .filter((key) => key !== 'timestamp')
      .reduce(
        (acc, key) => {
          acc[key] = key;
          return acc;
        },
        {} as Record<string, string>
      );
  }, [filteredMotorsAng]);

  const distributionHip = useMemo(() => {
    if (!raw) return null;
    return getFlexosTimeGraph(
      raw, // data
      selectedHipDistributionGraph, //dataType
      'FlexosH', //FlexoType
      t // t (translation function)
    );
  }, [raw, selectedHipDistributionGraph, t]);

  const distributionKnee = useMemo(() => {
    if (!raw) return null;
    return getFlexosTimeGraph(raw, selectedKneeDistributionGraph, 'FlexosK', t);
  }, [raw, selectedKneeDistributionGraph, t]);

  const showSomething =
    (distributionHip && hasValidData(distributionHip?.dataFlexosTime)) ||
    (distributionKnee && hasValidData(distributionKnee?.dataFlexosTime)) ||
    hasValidData(motorsAng);

  if (!showSomething) {
    return <EmptyState icon="pi pi-chart-pie" title={t('emptyMessage')} />;
  }

  return (
    <>
      <div className="flex flex-wrap sm:flex-nowrap gap-4 justify-content-center align-items-start mt-3">
        {distributionHip?.dataFlexosTime &&
          hasValidData(distributionHip.dataFlexosTime) && (
            <div className="w-full sm:w-6 max-w-40rem aspect-square flex flex-column flex-shrink-1">
              <GraphStepsDistribution
                data={distributionHip.dataFlexosTime}
                options={distributionHip.optionsFlexosTime}
              />
              <div className="flex justify-content-center align-items-center gap-3 mt-5">
                <div className="flex gap-2 mb-3">
                  <label className="font-bold block mb-2">
                    {t('pages.patients.viewPatient.sessions.steps')}
                  </label>
                  <Checkbox
                    checked={selectedHipDistributionGraph === 'steps'}
                    onChange={() => setSelectedHipDistributionGraph('steps')}
                  />
                </div>
                <div className="flex gap-2 mb-3">
                  <label className="font-bold block mb-2">
                    {t('pages.patients.viewPatient.sessions.time')}
                  </label>
                  <Checkbox
                    checked={selectedHipDistributionGraph === 'time'}
                    onChange={() => setSelectedHipDistributionGraph('time')}
                  />
                </div>
              </div>
            </div>
          )}

        {distributionKnee?.dataFlexosTime &&
          hasValidData(distributionKnee.dataFlexosTime) && (
            <div className="w-full sm:w-6 max-w-40rem aspect-square flex flex-column flex-shrink-1">
              <GraphStepsDistribution
                data={distributionKnee.dataFlexosTime}
                options={distributionKnee.optionsFlexosTime}
              />
              <div className="flex justify-content-center align-items-center gap-3 mt-5">
                <div className="flex gap-2 mb-3">
                  <label className="font-bold block mb-2">
                    {t('pages.patients.viewPatient.sessions.steps')}
                  </label>
                  <Checkbox
                    checked={selectedKneeDistributionGraph === 'steps'}
                    onChange={() => setSelectedKneeDistributionGraph('steps')}
                  />
                </div>
                <div className="flex gap-2 mb-3">
                  <label className="font-bold block mb-2">
                    {t('pages.patients.viewPatient.sessions.time')}
                  </label>
                  <Checkbox
                    checked={selectedKneeDistributionGraph === 'time'}
                    onChange={() => setSelectedKneeDistributionGraph('time')}
                  />
                </div>
              </div>
            </div>
          )}
      </div>

      <BrushGraphs
        data={motorsAng ?? []} // serie base de AUTO
        startIndex={props.startIndex}
        endIndex={props.endIndex}
        onBrushChange={props.onBrushChange}
      />

      {hasValidData(motorsAng) && (
        <RoleBasedMultiLineChart
          role={role}
          data={filteredMotorsAng}
          isAdminRole={(role) => role === ROLES.MARSI}
          titleAdmin={`${t('pages.maintenance.ingestion.motorsRef')} (°)`}
          titleUser={t('pages.reports.graphs.rom')}
          setLoading={noop}
          keyMapAdmin={keyMapAdmin}
          keyMapUser={keyMapUser}
          deviceModel={props.deviceModel}
          chartOptions={{
            yAxisTitle: t('pages.patients.viewPatient.anglesTitle'),
          }}
        />
      )}
    </>
  );
}

const AutoGraphs = memo(AutoGraphsImpl);
export default AutoGraphs;
